import React, { useState, useEffect } from "react";
import {
  GitBranch,
  Clock,
  ArrowRight,
  Server,
  Layers,
  AlertTriangle,
  Filter,
  MessageSquare,
  PenTool,
  Settings,
  ChevronRight,
  CheckCircle,
  Shield,
  Activity,
} from "lucide-react";
import Modal from "../ui/Modal";
import { buildStreamsFromSegments } from "../../logic/streamGenerator";
import campaignData from "../../data/campaignData.json";

const StreamsBuilderCard = ({ segments = [], existingStreams = [] }) => {
  const [activeStream, setActiveStream] = useState(null);
  const [activeTouch, setActiveTouch] = useState(null);
  const [streams, setStreams] = useState([]);

  // Access the narrative data from the JSON source of truth
  const narrative = campaignData.tab_narratives?.streams;

  useEffect(() => {
    // 1. Process Pre-built Streams (Legacy support)
    const prebuilt = existingStreams.map((s) => ({
      ...s,
      type: "Legacy",
      strategy: {
        cadence: s.cadence,
        tone: "Legacy",
        buying_question: "Unknown",
      },
      ops_checklist: ["Standard Scoring"],
    }));

    // 2. Generate Dynamic Streams via Engine
    const generated = buildStreamsFromSegments(segments);

    // 3. Merge List
    setStreams([...prebuilt, ...generated]);
  }, [segments, existingStreams]);

  const closeStreamModal = () => {
    setActiveStream(null);
    setActiveTouch(null);
  };

  return (
    <div className="space-y-8 font-sans animate-in fade-in duration-500">
      {/* 1. NARRATIVE PANEL (The Executive Advisory Layer) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="bg-slate-50 p-6 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg">
              <GitBranch size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {narrative?.title || "Stream Frameworks"}
              </h2>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                {narrative?.subtitle || "How Engagement is Orchestrated"}
              </p>
            </div>
          </div>
          <p className="text-slate-700 mt-2 max-w-4xl text-lg leading-relaxed">
            {narrative?.executive_overview}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-emerald-500" /> What You
              Get
            </h4>
            <ul className="space-y-2">
              {narrative?.what_you_get?.map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-600 flex items-start gap-2"
                >
                  <span className="mt-1.5 w-1 h-1 bg-slate-400 rounded-full shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 bg-slate-50/50">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <Shield size={16} className="text-amber-500" /> Why It Matters
            </h4>
            <p className="text-sm text-slate-600 leading-relaxed">
              {narrative?.why_it_matters}
            </p>
          </div>
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <Activity size={16} className="text-blue-500" /> Distinction
            </h4>
            <p className="text-sm text-slate-500 italic border-l-2 border-blue-200 pl-3">
              "{narrative?.difference}"
            </p>
          </div>
        </div>
      </div>

      {/* 2. OPERATIONAL STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
            <GitBranch size={24} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase">
              Active Streams
            </p>
            <p className="text-2xl font-bold text-slate-800">
              {streams.length}
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 rounded-lg text-emerald-600">
            <Server size={24} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase">
              Logic Engine
            </p>
            <p className="text-sm font-semibold text-slate-800">
              Policy-Based Generation
            </p>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-purple-50 rounded-lg text-purple-600">
            <Layers size={24} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase">
              Coverage
            </p>
            <p className="text-sm font-semibold text-slate-800">
              {segments.length > 0
                ? "Custom Segments Mapped"
                : "Using Default Frameworks"}
            </p>
          </div>
        </div>
      </div>

      {/* 3. STREAMS GRID */}
      <div className="grid grid-cols-1 gap-4">
        {streams.map((stream, idx) => (
          <div
            key={idx}
            className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow group cursor-pointer relative overflow-hidden"
            onClick={() => setActiveStream(stream)}
          >
            {/* Type Badge */}
            <div
              className={`absolute top-0 right-0 px-3 py-1 text-[10px] font-bold uppercase rounded-bl-lg ${
                stream.type === "Legacy"
                  ? "bg-slate-100 text-slate-500"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {stream.type}
            </div>

            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div
                  className={`mt-1 w-2 h-2 rounded-full ${
                    stream.status === "Live" ? "bg-emerald-500" : "bg-amber-400"
                  }`}
                />
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">
                    {stream.name}
                  </h3>
                  {stream.id && (
                    <p className="text-xs font-mono text-slate-400 mt-1">
                      {stream.id}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />{" "}
                      {stream.strategy?.cadence || "Standard"}
                    </span>
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-slate-600 text-xs font-medium">
                      {stream.strategy?.tone || "Standard Tone"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-slate-300 group-hover:text-emerald-600 transition-colors mt-8">
                <ArrowRight size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 4. MODALS (Main Stream Strategy + Detailed Drilldown) */}
      <Modal
        isOpen={!!activeStream}
        onClose={closeStreamModal}
        title={activeStream?.name || "Stream Strategy"}
      >
        {activeStream && !activeTouch && (
          <div className="space-y-8">
            {/* Header Summary */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex justify-between items-center">
              <div>
                <p className="text-xs text-slate-500 font-bold uppercase">
                  Execution ID
                </p>
                <p className="font-mono text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
                  {activeStream.id || "PENDING"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 font-bold uppercase">
                  Touch Density
                </p>
                <p className="font-semibold text-slate-800">
                  {activeStream.touches?.length || 0} Interactions
                </p>
              </div>
            </div>

            {/* Ops Rules */}
            {activeStream.ops_checklist && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="text-blue-600" size={18} />
                  <h4 className="font-bold text-blue-900 text-sm">
                    Ops Governance
                  </h4>
                </div>
                <ul className="space-y-2">
                  {activeStream.ops_checklist.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-blue-800"
                    >
                      <input
                        type="checkbox"
                        checked
                        readOnly
                        className="mt-1 rounded text-blue-600 opacity-60"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Journey Timeline */}
            <div>
              <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <GitBranch size={18} className="text-slate-400" />
                Stream Architecture
              </h4>
              <div className="relative pl-4 border-l-2 border-slate-100 space-y-6">
                {activeStream.touches?.map((touch, i) => (
                  <div
                    key={i}
                    className="relative cursor-pointer group"
                    onClick={() => setActiveTouch(touch)}
                  >
                    <div
                      className={`absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-white border-2 ${
                        touch.is_conditional
                          ? "border-amber-400"
                          : "border-emerald-500"
                      }`}
                    />
                    <div
                      className={`bg-white border rounded-lg p-4 shadow-sm transition-all hover:shadow-md ${
                        touch.is_conditional
                          ? "border-amber-200 bg-amber-50/50"
                          : "border-slate-200 hover:border-emerald-300"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-1 rounded">
                          Day {touch.day}: {touch.channel}
                        </span>
                        {touch.is_conditional && (
                          <span className="flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">
                            <AlertTriangle size={10} /> Conditional
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <h5 className="font-bold text-slate-800 text-sm mb-1">
                          {touch.subject}
                        </h5>
                        <ChevronRight
                          size={16}
                          className="text-slate-300 group-hover:text-emerald-500"
                        />
                      </div>
                      <p className="text-xs text-slate-500">
                        <span className="font-semibold">Goal:</span>{" "}
                        {touch.intent_goal}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* DETAILED TOUCH MODAL VIEW */}
        {activeTouch && (
          <div className="space-y-6 animate-in slide-in-from-right duration-300">
            <button
              onClick={() => setActiveTouch(null)}
              className="flex items-center text-sm text-slate-500 hover:text-emerald-600 mb-4"
            >
              <ArrowRight className="rotate-180 mr-1" size={16} /> Back to
              Stream
            </button>

            <div className="border-b border-slate-200 pb-4 mb-4">
              <h3 className="text-xl font-bold text-slate-900">
                {activeTouch.type}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Day {activeTouch.day} • {activeTouch.channel}
              </p>
            </div>

            {activeTouch.advisory_guide ? (
              <div className="space-y-6">
                {/* Buyer Message */}
                <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-bold text-slate-800">
                      Buyer Message Strategy
                    </h4>
                  </div>
                  <p className="text-sm font-medium text-slate-800 italic mb-4">
                    "{activeTouch.advisory_guide.marketer?.objective}"
                  </p>

                  {activeTouch.advisory_guide.marketer?.subjectLines && (
                    <div className="mb-4">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-2">
                        Recommended Subject Lines:
                      </p>
                      <ul className="list-disc pl-4 space-y-1">
                        {activeTouch.advisory_guide.marketer.subjectLines.map(
                          (line, i) => (
                            <li key={i} className="text-sm text-slate-700">
                              {line}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Actions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <PenTool className="w-4 h-4 text-blue-600" />
                      <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                        Marketer Actions
                      </h4>
                    </div>
                    <ul className="space-y-3">
                      {activeTouch.advisory_guide.marketer?.bodyBlocks?.map(
                        (block, i) => (
                          <li
                            key={i}
                            className="text-sm text-slate-600 flex gap-2"
                          >
                            <span className="text-blue-400">•</span>
                            {block}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Settings className="w-4 h-4 text-purple-600" />
                      <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                        MOPs Configuration
                      </h4>
                    </div>
                    <div className="space-y-3 text-sm text-slate-600">
                      <p>
                        <span className="text-purple-600 font-semibold">
                          Program:
                        </span>{" "}
                        {activeTouch.advisory_guide.mops?.program ||
                          "Standard_Nurture"}
                      </p>
                      <p>
                        <span className="text-purple-600 font-semibold">
                          Smart List:
                        </span>{" "}
                        {activeTouch.advisory_guide.mops?.smartList ||
                          "Standard Entry"}
                      </p>
                      <p>
                        <span className="text-purple-600 font-semibold">
                          Scoring:
                        </span>{" "}
                        {activeTouch.advisory_guide.mops?.scoring || "Standard"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 italic">
                No advisory details available for this touch.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StreamsBuilderCard;
